import type { Editor } from '@tiptap/core';
import { store } from '@/state/store';
import type { DocComment } from '@/state/types';
import { SET_COMMENTS_META } from './commentHistory';
// Operace s komentáři nejsou obsahové úpravy — sledování změn je musí
// ignorovat, jinak by se označení textu komentářem zaznamenalo jako změna.
import { trackChangesKey } from './trackChanges';

// ═══════════════════════════════════════════════════════════════
// Správa komentářů (Fáze 3) — logika nad store.doc.comments.
//
// Data komentářů žijí ve store; vazba na text je přes CommentMark
// (data-comment-id). Tenhle modul je jediné místo, které komentáře mění —
// UI panel i DOCX import volají jen tyhle funkce.
// ═══════════════════════════════════════════════════════════════

/** Vrátí komentáře aktuálního dokumentu (nikdy ne undefined). */
export function getComments(): DocComment[] {
  return store.get().doc.comments || [];
}

/** Nejvyšší použité ID + 1 (Word chce unikátní číselná ID). */
function nextId(): number {
  const list = getComments();
  return list.length ? Math.max(...list.map((c) => c.id)) + 1 : 0;
}

/** Iniciály z celého jména („Tomáš Nový" → „TN"). */
export function initialsFrom(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return '?';
  return parts.slice(0, 2).map((p) => p[0].toUpperCase()).join('');
}

/** Autor podle přihlášeného uživatele; fallback když není přihlášený. */
function currentAuthor(): { author: string; initials: string } {
  const user = store.get().user;
  const author = user?.name || user?.email || 'Neznámý autor';
  return { author, initials: initialsFrom(author) };
}

/**
 * Zapíše nový stav komentářů. Když je k dispozici editor, jde změna přes
 * ProseMirror transakci — díky tomu ji zachytí undo historie a Zpět/Znovu
 * funguje i na komentáře (viz CommentHistory plugin). Store se aktualizuje
 * rovnou, aby UI reagovalo okamžitě.
 */
function commit(list: DocComment[], editor?: Editor): void {
  store.patchDoc({ comments: list, dirty: true });
  if (editor) {
    const tr = editor.state.tr
      .setMeta(SET_COMMENTS_META, list)
      .setMeta(trackChangesKey, true);
    // Transakce nemění dokument, ale musí projít historií — proto ji
    // neoznačujeme jako addToHistory: false.
    editor.view.dispatch(tr);
  }
}

/**
 * Vytvoří komentář k aktuálnímu výběru v editoru a označí text markem.
 * Vrací nový komentář, nebo null když není co komentovat (prázdný výběr).
 */
export function addComment(editor: Editor, text: string): DocComment | null {
  const { from, to } = editor.state.selection;
  if (from === to) return null; // prázdný výběr — není co komentovat
  const trimmed = text.trim();
  if (!trimmed) return null;

  const { author, initials } = currentAuthor();
  const comment: DocComment = {
    id: nextId(),
    text: trimmed,
    author,
    initials,
    date: new Date().toISOString(),
  };

  // Komentář i mark musí být v JEDNÉ transakci — jinak by je undo vracelo
  // po částech (nejdřív mark, pak komentář) a mezistav by byl nekonzistentní.
  const list = [...getComments(), comment];
  store.patchDoc({ comments: list, dirty: true });
  editor
    .chain()
    .focus()
    .setCommentMark(comment.id)
    .command(({ tr }) => {
      tr.setMeta(SET_COMMENTS_META, list);
      tr.setMeta(trackChangesKey, true); // není to obsahová úprava
      return true;
    })
    .run();
  return comment;
}

/** Přidá odpověď do vlákna pod daný komentář. */
export function replyToComment(parentId: number, text: string, editor?: Editor): DocComment | null {
  const trimmed = text.trim();
  if (!trimmed) return null;
  const list = getComments();
  const parent = list.find((c) => c.id === parentId);
  if (!parent) return null;

  // Odpověď na odpověď patří pořád do stejného vlákna (Word je plochý model:
  // parentId ukazuje na kořen vlákna, ne na libovolnou úroveň).
  const rootId = parent.parentId ?? parent.id;

  const { author, initials } = currentAuthor();
  const reply: DocComment = {
    id: nextId(),
    text: trimmed,
    author,
    initials,
    date: new Date().toISOString(),
    parentId: rootId,
  };
  commit([...list, reply], editor);
  return reply;
}

/** Přepne stav vyřešení celého vlákna (kořen + odpovědi). */
export function toggleResolved(commentId: number, editor?: Editor): void {
  const list = getComments();
  const target = list.find((c) => c.id === commentId);
  if (!target) return;
  const rootId = target.parentId ?? target.id;
  const nextState = !isThreadResolved(rootId);

  commit(
    list.map((c) =>
      c.id === rootId || c.parentId === rootId ? { ...c, resolved: nextState } : c,
    ),
    editor,
  );
}

/** Je vlákno vyřešené? (rozhoduje kořenový komentář) */
export function isThreadResolved(rootId: number): boolean {
  const root = getComments().find((c) => c.id === rootId);
  return !!root?.resolved;
}

/**
 * Smaže komentář. U kořene vlákna smaže i všechny odpovědi a odstraní mark
 * z textu; u odpovědi jen ji samotnou.
 */
export function deleteComment(editor: Editor, commentId: number): void {
  const list = getComments();
  const target = list.find((c) => c.id === commentId);
  if (!target) return;

  if (target.parentId != null) {
    // Odpověď — smaž jen ji, mark zůstává (patří kořeni).
    commit(list.filter((c) => c.id !== commentId), editor);
    return;
  }

  // Kořen vlákna — smaž vlákno celé a odstraň mark z textu. Obojí v jedné
  // transakci, aby to undo vrátilo najednou.
  const next = list.filter((c) => c.id !== commentId && c.parentId !== commentId);
  store.patchDoc({ comments: next, dirty: true });
  editor
    .chain()
    .unsetCommentMark(commentId)
    .command(({ tr }) => {
      tr.setMeta(SET_COMMENTS_META, next);
      tr.setMeta(trackChangesKey, true);
      return true;
    })
    .run();
}

/**
 * Kořenové komentáře seřazené podle POZICE v dokumentu (jako ve Wordu),
 * ne podle času vzniku. Komentáře bez vazby na text (mark se ztratil) jdou
 * na konec, seřazené podle data.
 */
export function rootComments(editor?: Editor): DocComment[] {
  const roots = getComments().filter((c) => c.parentId == null);
  if (!editor) {
    return [...roots].sort((a, b) => a.date.localeCompare(b.date));
  }

  // Zjisti pozici prvního výskytu marku pro každé ID.
  const posById = new Map<number, number>();
  editor.state.doc.descendants((node, pos) => {
    if (!node.isText) return;
    node.marks.forEach((m) => {
      if (m.type.name !== 'commentMark') return;
      const id = m.attrs.commentId as number | null;
      if (id == null) return;
      if (!posById.has(id)) posById.set(id, pos);
    });
  });

  return [...roots].sort((a, b) => {
    const pa = posById.get(a.id);
    const pb = posById.get(b.id);
    // Oba mají vazbu → podle pozice v textu.
    if (pa != null && pb != null) return pa - pb;
    // Jen jeden má vazbu → ten s vazbou první.
    if (pa != null) return -1;
    if (pb != null) return 1;
    // Ani jeden → podle data.
    return a.date.localeCompare(b.date);
  });
}

/** Odpovědi daného vlákna, seřazené podle data. */
export function repliesOf(rootId: number): DocComment[] {
  return getComments()
    .filter((c) => c.parentId === rootId)
    .sort((a, b) => a.date.localeCompare(b.date));
}

/**
 * Smaže VŠECHNY komentáře v dokumentu a odstraní jejich marky z textu.
 * Vrací počet smazaných vláken.
 */
export function deleteAllComments(editor: Editor): number {
  const roots = getComments().filter((c) => c.parentId == null);
  if (!roots.length) return 0;

  // VŠECHNY marky i data musí být v JEDNÉ transakci — jinak by každý mark
  // vytvořil vlastní krok historie a undo by vrátilo jen ten poslední.
  store.patchDoc({ comments: [], dirty: true });
  const chain = editor.chain();
  for (const root of roots) chain.unsetCommentMark(root.id);
  chain
    .command(({ tr }) => {
      tr.setMeta(SET_COMMENTS_META, []);
      tr.setMeta(trackChangesKey, true);
      return true;
    })
    .run();
  return roots.length;
}

/**
 * Smaže jen vyřešená vlákna (kořen s resolved + jejich odpovědi).
 * Vrací počet smazaných vláken.
 */
export function deleteResolvedComments(editor: Editor): number {
  const list = getComments();
  const resolvedRoots = list.filter((c) => c.parentId == null && c.resolved);
  if (!resolvedRoots.length) return 0;

  const removeIds = new Set<number>();
  for (const root of resolvedRoots) {
    removeIds.add(root.id);
    list.filter((c) => c.parentId === root.id).forEach((r) => removeIds.add(r.id));
  }

  // Jedna transakce pro všechny marky i data (viz deleteAllComments).
  const next = list.filter((c) => !removeIds.has(c.id));
  store.patchDoc({ comments: next, dirty: true });
  const chain = editor.chain();
  for (const root of resolvedRoots) chain.unsetCommentMark(root.id);
  chain
    .command(({ tr }) => {
      tr.setMeta(SET_COMMENTS_META, next);
      tr.setMeta(trackChangesKey, true);
      return true;
    })
    .run();
  return resolvedRoots.length;
}

/** Počet vyřešených vláken (pro UI — zda má smysl nabízet akci). */
export function resolvedThreadCount(): number {
  return getComments().filter((c) => c.parentId == null && c.resolved).length;
}

/** Počet všech vláken. */
export function threadCount(): number {
  return getComments().filter((c) => c.parentId == null).length;
}

/**
 * Odstraní z textu marky, které ukazují na neexistující komentář (osiřelé).
 * Vzniknou např. když se komentář smaže cestou, která mark neuklidí, nebo
 * po importu, kde se ID přečíslují. Osiřelý mark je nebezpečný: při exportu
 * by vygeneroval referenci bez komentáře a Word by soubor odmítl otevřít.
 *
 * Vrací počet uklizených marků.
 */
export function cleanOrphanMarks(editor: Editor): number {
  const validIds = new Set(getComments().map((c) => c.id));
  const orphans = new Set<number>();

  editor.state.doc.descendants((node) => {
    if (!node.isText) return;
    node.marks.forEach((m) => {
      if (m.type.name !== 'commentMark') return;
      const id = m.attrs.commentId as number | null;
      if (id == null || !validIds.has(id)) orphans.add(id ?? -1);
    });
  });

  for (const id of orphans) {
    editor
      .chain()
      .unsetCommentMark(id)
      .command(({ tr }) => {
        tr.setMeta(trackChangesKey, true);
        return true;
      })
      .run();
  }
  return orphans.size;
}

/** Nahradí celý seznam komentářů (používá DOCX import). */
export function setComments(list: DocComment[]): void {
  store.patchDoc({ comments: list });
}

/**
 * Zjistí ID komentářů na aktuální pozici kurzoru — pro zvýraznění
 * odpovídajícího vlákna v postranním panelu.
 */
export function commentIdsAtSelection(editor: Editor): number[] {
  const { from, to } = editor.state.selection;
  const ids = new Set<number>();
  editor.state.doc.nodesBetween(from, to, (node) => {
    if (!node.isText) return;
    node.marks.forEach((m) => {
      if (m.type.name === 'commentMark' && m.attrs.commentId != null) {
        ids.add(m.attrs.commentId as number);
      }
    });
  });
  return [...ids];
}
